/**
 * /dashboard/posh/complaints — POSH complaints landing.
 *
 * Two surfaces, deliberately separated by the 2FA boundary:
 *  - "My complaints" — GET /posh/my-complaints (own, NOT 2FA-gated).
 *  - "ICC register"  — GET /posh/complaints (STRICT 2FA). 2026-10: an
 *    HR/admin without a committee seat gets REGISTER rows (no identities,
 *    no narrative — POSH Act 2013 §16); a complaint against the viewer is
 *    never listed. HR admins also find the public intake link here.
 *    2026-10 persona round: the register tab renders ONLY for a viewer
 *    who can see it (GET /posh/committee viewer.can_view_register — a
 *    seat in office or HR/admin). Everyone else gets "My complaints" +
 *    the "Who sees your complaint" note (ComplaintPrivacyNotice).
 *
 * Migration note (Phase 3f — 2026-06-01): the new + detail/manage
 * sub-routes are now ported; <a href> swapped for typed Link.
 */

import { createFileRoute, Link, useLocation, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FileWarning, Plus, ShieldAlert } from "lucide-react";
import { poshApi, toApiError } from "@/lib/api";
import { useFeatures } from "@/lib/auth";
import {
  is2FARequiredError,
  poshKeys,
  useCommittee,
  useComplaints,
  useMyComplaints,
} from "@/hooks/use-posh";
import { ComplaintPrivacyNotice } from "@/components/posh/complaint-privacy-notice";
import { complainantLabel, fmtDate } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PublicIntakeCard } from "@/components/posh/public-intake-card";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import type { ComplaintDTO } from "@/types/posh";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/complaints/")({
  // A9 hover-prefetch. Primary = "My complaints" (the default tab; own
  // rows, NOT 2FA-gated). The ICC register read is deliberately NOT
  // prefetched — it sits behind the strict-TOTP boundary and would 403
  // for any non-enrolled caller.
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData({
        queryKey: poshKeys.myComplaints(undefined),
        queryFn: ({ signal }) => poshApi.listMyComplaints(undefined, signal),
      })
      .catch(() => undefined),
  component: ComplaintsRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ComplaintRow({
  c,
  variant,
}: {
  c: ComplaintDTO;
  /** "own" → /dashboard/posh/complaints/$id; "manage" → that + /manage. */
  variant: "own" | "manage";
}) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const body = (
    <Card className="transition-colors group-hover:border-primary/40">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">
              {c.complaint_number}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {c.view === "register"
                ? `Filed ${fmtDate(c.created_at)} · details and identities withheld (committee only)`
                : `Against ${c.respondent_name ?? "—"} · Incident ${fmtDate(
                    c.incident_date,
                  )} · ${complainantLabel(c)}`}
            </p>
          </div>
          <StatusBadge status={c.status} />
        </div>
      </CardHeader>
    </Card>
  );
  if (variant === "manage") {
    return (
      <Link
        to="/app/$tenantSlug/posh/complaints/$id/manage"
        params={{tenantSlug, id: String(c.id)}}
        className="group block"
      >
        {body}
      </Link>
    );
  }
  return (
    <Link
      to="/app/$tenantSlug/posh/complaints/$id"
      params={{tenantSlug, id: String(c.id)}}
      className="group block"
    >
      {body}
    </Link>
  );
}

function MyComplaints() {
  const q = useMyComplaints();
  if (q.isLoading) return <ListSkeleton />;
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load your complaints</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }
  const items = q.data?.complaints ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon={FileWarning}
        title="You have not filed any complaints"
        description={'Use "File a complaint" above to raise one — you can then track its progress here.'}
      />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <ComplaintRow
          key={c.id}
          c={c}
          variant="own"
        />
      ))}
    </div>
  );
}

function ICCRegister() {
  const q = useComplaints();

  if (q.isLoading) return <ListSkeleton />;

  if (q.isError && is2FARequiredError(q.error)) {
    return <TwoFactorRequired surface="the ICC complaint register" />;
  }
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the complaint register</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }

  const items = q.data?.complaints ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="No Complaints in the Committee Register"
        description="No complaints are visible to you yet. Any complaint raised for the committee to review will appear here."
      />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <ComplaintRow
          key={c.id}
          c={c}
          variant="manage"
        />
      ))}
    </div>
  );
}

function ComplaintsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  // Manage → POSH → Complaints links here with ?view=register (the
  // committee's register; lib/surfaces.ts) — open on that tab.
  const search: Record<string, unknown> = useLocation({ select: (l) => l.search });
  const view = search.view;
  const [tab, setTab] = useState(view === "register" ? "icc" : "mine");
  useEffect(() => {
    setTab(view === "register" ? "icc" : "mine");
  }, [view]);
  const isAdmin = usePoshAdmin();
  // 2026-10 persona round: the committee register (and its 2FA prompt)
  // is shown ONLY to someone who can see it — a committee member in
  // office or HR/company admin (server-resolved, GET /posh/committee).
  // An ordinary employee sees their own complaints and a plain "who sees
  // your complaint" note instead. The register routes enforce access
  // server-side regardless.
  // Not before the plan check: without POSH the read is refused
  // (upgrade_required) and the page shows the plan notice instead.
  const { has } = useFeatures();
  const committee = useCommittee(has("posh"));
  const showRegister = committee.data?.viewer.can_view_register === true;
  return (
    <PoshModuleShell
      title="Complaints"
      description={
        showRegister
          ? "File and track a POSH complaint. The committee register is protected by two-factor authentication."
          : "File a POSH complaint and follow its progress. Your complaint goes only to the Internal Committee."
      }
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/complaints/new" params={{ tenantSlug }}>
            <Plus className="h-4 w-4" />
            File a Complaint
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {showRegister ? (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="mine">My Complaints</TabsTrigger>
              <TabsTrigger value="icc">ICC Register</TabsTrigger>
            </TabsList>
            <TabsContent value="mine" className="mt-4">
              <MyComplaints />
            </TabsContent>
            <TabsContent value="icc" className="mt-4">
              <ICCRegister />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-6">
            <section>
              <h2 className="mb-3 text-sm font-medium text-muted-foreground">
                My Complaints
              </h2>
              <MyComplaints />
            </section>
            <ComplaintPrivacyNotice />
          </div>
        )}
        {/* The public / anonymous intake link (QA 2026-10: it was only
            under Policy). HR-admin only, like on the Policy page. */}
        {isAdmin && (
          <div className="mt-8">
            <PublicIntakeCard />
          </div>
        )}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
